import { useEffect, useRef, useCallback } from "react";

const PALETTE = [
  [99, 91, 255],   // #635BFF
  [0, 212, 255],   // #00D4FF
  [129, 140, 248], // #818CF8
  [79, 70, 229],   // #4F46E5
  [56, 189, 248],  // #38BDF8
];

function lerp(a, b, t) { return a + (b - a) * t; }

export default function HeroParticles() {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    mouse: { x: 0.5, y: 0.5 },
    targetMouse: { x: 0.5, y: 0.5 },
    particles: [],
    rays: [],
    dpr: 1,
    w: 0,
    h: 0,
    time: 0,
    animId: null,
    reducedMotion: false,
    isMobile: false,
  });

  const init = useCallback(() => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    s.dpr = Math.min(window.devicePixelRatio || 1, 2);
    s.isMobile = window.innerWidth < 768;
    s.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const rect = canvas.parentElement.getBoundingClientRect();
    s.w = rect.width;
    s.h = rect.height;
    canvas.width = s.w * s.dpr;
    canvas.height = s.h * s.dpr;
    canvas.style.width = s.w + "px";
    canvas.style.height = s.h + "px";

    // Center point for the radial burst (offset right and slightly above center)
    const cx = s.w * 0.62;
    const cy = s.h * 0.42;

    // Generate rays
    const rayCount = s.isMobile ? 40 : 80;
    s.rays = [];
    for (let i = 0; i < rayCount; i++) {
      const angle = (Math.PI * 2 * i) / rayCount + (Math.random() - 0.5) * 0.04;
      const length = (s.isMobile ? 120 : 250) + Math.random() * (s.isMobile ? 80 : 200);
      const colorIdx = Math.floor(Math.random() * PALETTE.length);
      s.rays.push({
        angle,
        length,
        baseLength: length,
        color: PALETTE[colorIdx],
        width: 0.6 + Math.random() * 1.0,
        phase: Math.random() * Math.PI * 2,
        speed: 0.3 + Math.random() * 0.5,
        cx, cy,
      });
    }

    // Generate particles (dots at ray tips + floating ambient)
    const particleCount = s.isMobile ? 25 : 60;
    s.particles = [];
    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * (s.isMobile ? 160 : 320);
      const colorIdx = Math.floor(Math.random() * PALETTE.length);
      s.particles.push({
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        baseX: cx + Math.cos(angle) * dist,
        baseY: cy + Math.sin(angle) * dist,
        radius: 1 + Math.random() * 2.5,
        color: PALETTE[colorIdx],
        phase: Math.random() * Math.PI * 2,
        speed: 0.2 + Math.random() * 0.6,
        drift: 0.3 + Math.random() * 0.7,
      });
    }
  }, []);

  useEffect(() => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    init();

    const ctx = canvas.getContext("2d", { alpha: true });

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      s.targetMouse.x = (e.clientX - rect.left) / rect.width;
      s.targetMouse.y = (e.clientY - rect.top) / rect.height;
    };

    const handleResize = () => {
      init();
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    const draw = (timestamp) => {
      s.time = timestamp * 0.001;

      // Smooth mouse lerp
      s.mouse.x = lerp(s.mouse.x, s.targetMouse.x, 0.04);
      s.mouse.y = lerp(s.mouse.y, s.targetMouse.y, 0.04);

      ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
      ctx.clearRect(0, 0, s.w, s.h);

      if (s.reducedMotion) {
        drawStatic(ctx, s);
      } else {
        drawAnimated(ctx, s);
      }

      s.animId = requestAnimationFrame(draw);
    };

    s.animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(s.animId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
    };
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 1 }}
      aria-hidden="true"
    />
  );
}

function drawStatic(ctx, s) {
  // Reduced motion: draw a simple static starburst
  const cx = s.w * 0.62;
  const cy = s.h * 0.42;

  s.rays.forEach((ray) => {
    const endX = cx + Math.cos(ray.angle) * ray.baseLength * 0.7;
    const endY = cy + Math.sin(ray.angle) * ray.baseLength * 0.7;
    const [r, g, b] = ray.color;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(endX, endY);
    ctx.strokeStyle = `rgba(${r},${g},${b},0.08)`;
    ctx.lineWidth = ray.width * 0.6;
    ctx.stroke();
  });

  // Center glow
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 8);
  grad.addColorStop(0, "rgba(99,91,255,0.15)");
  grad.addColorStop(1, "rgba(99,91,255,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0, Math.PI * 2);
  ctx.fill();
}

function drawAnimated(ctx, s) {
  const t = s.time;
  const mx = (s.mouse.x - 0.5) * 30; // Subtle mouse parallax offset
  const my = (s.mouse.y - 0.5) * 20;

  // Draw rays from center
  s.rays.forEach((ray) => {
    const breathe = Math.sin(t * ray.speed + ray.phase) * 0.15 + 1;
    const len = ray.baseLength * breathe;

    // Mouse repulsion: rays near the cursor subtly deflect
    const rayEndX = ray.cx + mx * 0.3 + Math.cos(ray.angle) * len;
    const rayEndY = ray.cy + my * 0.3 + Math.sin(ray.angle) * len;
    const startX = ray.cx + mx * 0.15;
    const startY = ray.cy + my * 0.15;

    const shimmer = 0.07 + Math.sin(t * ray.speed * 1.5 + ray.phase) * 0.05;
    const [r, g, b] = ray.color;

    // Gradient along ray
    const grad = ctx.createLinearGradient(startX, startY, rayEndX, rayEndY);
    grad.addColorStop(0, `rgba(${r},${g},${b},${shimmer * 3})`);
    grad.addColorStop(0.5, `rgba(${r},${g},${b},${shimmer * 1.8})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(rayEndX, rayEndY);
    ctx.strokeStyle = grad;
    ctx.lineWidth = ray.width;
    ctx.stroke();

    // Dot at ray tip
    const dotAlpha = 0.2 + Math.sin(t * ray.speed * 2 + ray.phase) * 0.12;
    const dotR = 1.5 + Math.sin(t * ray.speed + ray.phase) * 0.6;
    ctx.beginPath();
    ctx.arc(rayEndX, rayEndY, dotR, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${dotAlpha})`;
    ctx.fill();
  });

  // Draw floating particles
  s.particles.forEach((p) => {
    const wobbleX = Math.sin(t * p.speed + p.phase) * p.drift * 3;
    const wobbleY = Math.cos(t * p.speed * 0.8 + p.phase + 1) * p.drift * 3;
    const px = p.baseX + wobbleX + mx * p.drift * 0.5;
    const py = p.baseY + wobbleY + my * p.drift * 0.5;

    const alpha = 0.15 + Math.sin(t * p.speed * 1.3 + p.phase) * 0.1;
    const size = p.radius * (0.9 + Math.sin(t * p.speed + p.phase) * 0.2);
    const [r, g, b] = p.color;

    // Glow
    const glow = ctx.createRadialGradient(px, py, 0, px, py, size * 3);
    glow.addColorStop(0, `rgba(${r},${g},${b},${alpha * 0.5})`);
    glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(px, py, size * 3, 0, Math.PI * 2);
    ctx.fill();

    // Core dot
    ctx.beginPath();
    ctx.arc(px, py, size, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${r},${g},${b},${alpha * 1.5})`;
    ctx.fill();
  });

  // Center glow (breathing)
  const cx = s.w * 0.62 + mx * 0.15;
  const cy = s.h * 0.42 + my * 0.15;
  const glowSize = 16 + Math.sin(t * 0.5) * 4;
  const centerGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowSize);
  centerGlow.addColorStop(0, `rgba(99,91,255,${0.18 + Math.sin(t * 0.5) * 0.06})`);
  centerGlow.addColorStop(0.5, `rgba(0,212,255,${0.06 + Math.sin(t * 0.7) * 0.03})`);
  centerGlow.addColorStop(1, "rgba(99,91,255,0)");
  ctx.fillStyle = centerGlow;
  ctx.beginPath();
  ctx.arc(cx, cy, glowSize, 0, Math.PI * 2);
  ctx.fill();
}
